// src/components/professor/ClassFormModal.js
import React, { useState } from 'react';
import { Switch } from '@headlessui/react';


const ClassFormModal = ({ closeModal, handleAddClass, theme }) => {
  const [newClass, setNewClass] = useState({
    class_name: '',
    start_date: '',
    end_date: '',
    statsaccess: true,
    gradeaccess: true,
    sessionaccess: true,
    dataDeletionAck: false,
    aiReporting: true,
    quizFormat: 'fixed',  // or 'dynamic'
  });


  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setNewClass(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };


  const onSubmit = (e) => {
    e.preventDefault();
    const { start_date, end_date } = newClass;
    if (new Date(start_date) > new Date(end_date)) {
      alert('End Date must be after Start Date.');
      return;
    }

    if (!newClass.dataDeletionAck) {
      alert('Please acknowledge data deletion before creating the class.');
      return;
    }

    handleAddClass(newClass);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div
        className="p-6 rounded-2xl shadow-lg w-full max-w-lg mx-4 bg-[var(--card-bg)] text-[var(--text-color)] transition-colors"
      >
        <button
          onClick={closeModal}
          className="text-2xl font-bold float-right focus:outline-none"
          style={{ color: "var(--primary-color)" }}
          onMouseOver={(e) => (e.currentTarget.style.color = "var(--hover-color)")}
          onMouseOut={(e) => (e.currentTarget.style.color = "var(--primary-color)")}
        >
          &times;
        </button>

        <h2 className="text-3xl font-semibold mb-6">Register New Class</h2>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Class Name */}
          <div>
            <label htmlFor="class_name" className="block mb-1 font-medium">
              Class Name
            </label>
            <input
              type="text"
              id="class_name"
              name="class_name"
              placeholder="Enter class name"
              value={newClass.class_name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--card-bg)] text-[var(--text-color)] placeholder-[var(--muted-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] transition-colors"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="block mb-1 font-medium">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={newClass.start_date}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] transition-colors"
              />
            </div>
            <div>
              <label htmlFor="end_date" className="block mb-1 font-medium">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={newClass.end_date}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] transition-colors"
              />
            </div>
          </div>

          {/* Slide‐Toggles in two‐column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: "statsaccess", label: "Stats Access" },
              { name: "gradeaccess", label: "Grade Access" },
              { name: "sessionaccess", label: "Session Access" },
              // { name: "aiReporting", label: "AI Reporting" },
            ].map(({ name, label }) => (
              <div key={name} className="flex items-center">
                <Switch
                  checked={newClass[name]}
                  onChange={(val) =>
                    setNewClass((prev) => ({ ...prev, [name]: val }))
                  }
                  className={`
                  ce-mini-switch relative inline-flex items-center h-4 w-7 rounded-full transition-colors
                  border border-[var(--border-color)]
                  focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                  ${newClass[name]
                      ? 'bg-[var(--primary-color)]'
                      : 'bg-[var(--muted-color)]'
                    }
                `}
                >

                  <span
                    className={`
                    ce-mini-switch-knob inline-block h-2.5 w-2.5 transform rounded-full transition-transform shadow-sm
                    bg-[var(--card-bg)]
                    ${newClass[name]
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                      }
                  `}
                  />
                </Switch>
                <label htmlFor={name} className="ml-3 select-none">
                  {label}
                </label>
              </div>
            ))}
          </div>

          {/* Acknowledge Deletion (checkbox) */}
          <div className="flex items-center">
            <input
              id="dataDeletionAck"
              name="dataDeletionAck"
              type="checkbox"
              checked={newClass.dataDeletionAck}
              onChange={handleInputChange}
              className="h-5 w-5 accent-[var(--primary-color)]"
            />
            <label htmlFor="dataDeletionAck" className="ml-2 text-sm select-none">
              I understand all data will be deleted two weeks after end date.
            </label>
          </div>

          {/* Quiz Format */}
          <div>
            <label htmlFor="quizFormat" className="block mb-1 font-medium">
              Quiz Format
            </label>
            <select
              id="quizFormat"
              name="quizFormat"
              value={newClass.quizFormat}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--card-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] transition-colors"
            >
              <option value="fixed">5 Fixed Multiple-Choice</option>
              <option value="dynamic">Dynamic Input</option>
            </select>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full py-3 rounded-lg font-semibold transition-colors"
            style={{ backgroundColor: "var(--primary-color)", color: "var(--nav-active-text)" }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--hover-color)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--primary-color)")
            }
          >
            Create Class
          </button>
        </form>
      </div>
    </div>
  );

};

export default ClassFormModal;
